import React, { useCallback, useContext, useMemo, useState } from 'react';
import pluralize from 'pluralize';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Spinner } from 'js/design_system/Spinner';
import Badge from 'js/design_system/Badge';
import styled from 'astroturf/react';
import { trackClick } from 'js/utils/trackClick';
import { WorkData } from '../types';
import type { CitationReport } from '../types';
import WorkCard from './WorkCard';
import { getDefaultSortSettings } from '../utils/sortDefaults';
import { SortSettings } from './SortPanel';
import { filterStreamingWorks } from '../utils/filterStreamingWorks';
import { getPriorityLabel, getPriorityColor } from '../utils/claimPriority';
import { CiterightContext } from '../CiterightContextProvider';

const Card = styled.div`
  border: 1px solid var(--stroke-light);
  border-radius: 8px;
  padding: 12px;
  background: var(--background-white, #ffffff);

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const ReferenceLink = styled.button`
  @import 'stylesheets/design_system/body';

  text-align: left;
  background: none;
  border: none;
  color: var(--brand-colors-blue-600);
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font: inherit;
  font-size: 14px;
  display: block;
  margin-bottom: 4px;

  &:hover {
    color: var(--brand-colors-blue-700);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

interface ClaimCardProps {
  claim:
    | CitationReport['claims'][number]
    | Omit<CitationReport['claims'][number], 'actions'>;
  index: number;
  references: WorkData[];
  onClaimSelect: (claimId: string) => void;
  onDismiss: (claimId: string) => void;
  onReferenceScroll: (referenceId: string) => void;
  onWorkScroll?: (workId: string) => void;
  highlightedWorkId?: string | null;
  showDismissButton?: boolean;
  showChatButton?: boolean;
  pageContext?: 'mini_report' | 'inline_report' | 'preprint_report';
}

const ClaimCard = ({
  claim,
  index,
  references,
  onClaimSelect,
  onDismiss,
  onReferenceScroll,
  highlightedWorkId,
  showDismissButton = true,
  showChatButton = true,
  pageContext,
}: ClaimCardProps) => {
  const context = useContext(CiterightContext);
  // Sort and slice works based on rerank data availability
  const works = useMemo(() => {
    const allWorks = claim.search?.suggested_works || [];
    if (allWorks.length === 0) return [];

    // Filter works based on classification (show report works if any exist, otherwise fallbacks)
    const filteredWorks = filterStreamingWorks(allWorks);
    if (filteredWorks.length === 0) return [];

    // Get default sort settings
    const sortSettings: SortSettings = getDefaultSortSettings();

    // If sorting by relevance_score (academia sort), apply sorting
    if (sortSettings.field === 'relevance_score') {
      const sorted = [...filteredWorks].sort((a, b) => {
        const aValue = a.relevance_score ?? 0;
        const bValue = b.relevance_score ?? 0;
        return bValue - aValue; // Descending order
      });
      return sorted.slice(0, 3);
    }

    // Otherwise, keep original order
    return filteredWorks.slice(0, 3);
  }, [claim.search?.suggested_works]);

  const handleDismiss = useCallback(() => {
    trackClick({
      source: 'citeright.claim_no_citations_needed_button',
      entityId: context?.reportId ?? undefined,
    });
    onDismiss(claim.id);
  }, [claim.id, context?.reportId, onDismiss]);

  const handleReview = useCallback(() => {
    trackClick({
      source: 'citeright.claim_view_more_button',
      entityId: context?.reportId ?? undefined,
    });
    onClaimSelect(claim.id);
  }, [claim.id, context?.reportId, onClaimSelect]);

  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = useCallback(() => {
    trackClick({
      source: 'citeright.claim_search_button',
      entityId: context?.reportId ?? undefined,
    });
    if ('actions' in claim) {
      setIsSearching(true);
      const searchDeferred = claim.actions.search();
      searchDeferred.promise
        .catch((error) => {
          console.error('Failed to search claim:', error);
        })
        .finally(() => {
          setIsSearching(false);
        });
    }
  }, [claim, context?.reportId]);

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'nowrap',
        }}
      >
        <Body size="sm" style={{ color: 'var(--text-secondary)', flex: 1 }}>
          #{index + 1} - {claim.category}: {claim.reason}
        </Body>
        {(claim.priority || claim.top_claims_count) && (
          <Badge
            text={getPriorityLabel(claim.priority, claim.top_claims_count)}
            backgroundColor={
              getPriorityColor(claim.priority, claim.top_claims_count)
                .backgroundColor
            }
            color={
              getPriorityColor(claim.priority, claim.top_claims_count).color
            }
            style={{ flexShrink: 0 }}
          />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{
            backgroundColor: 'var(--background-beige-light)',
            padding: '8px',
            borderLeft: `4px solid ${
              getPriorityColor(claim.priority, claim.top_claims_count)
                .backgroundColor || 'transparent'
            }`,
            width: '100%',
          }}
        >
          <Body
            size="md"
            style={{
              fontStyle: 'italic',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            "{claim.text}"
          </Body>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {claim.search?.suggested_works &&
          claim.search?.suggested_works.length > 0 ? (
            <Button size="sm" onClick={handleReview} iconRight="arrow_forward">
              View more
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleSearch}
              iconRight="search"
              disabled={claim.search?.status === 'pending' || isSearching}
            >
              Search for citations
            </Button>
          )}
          {showDismissButton && (
            <Button
              size="sm"
              secondary
              onClick={handleDismiss}
              iconRight="close"
            >
              No citations needed
            </Button>
          )}
        </div>
      </div>

      {claim.search?.message_to_user && (
        <Body
          size="sm"
          style={{
            color: 'var(--text-secondary)',
            marginTop: 4,
          }}
        >
          {claim.search?.message_to_user}
        </Body>
      )}

      {context?.isPolling && !claim.search?.done && (
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Spinner size={16} />
          <Body size="sm" className="ds2-5-body-link-secondary">
            Finding references...
          </Body>
        </div>
      )}

      {claim.search?.done &&
        claim.search?.suggested_works !== null &&
        works.length === 0 && (
          <div style={{ textAlign: 'center', margin: '12px 0' }}>
            <Body size="md" style={{ color: 'var(--text-secondary)' }}>
              No references found for this query.
            </Body>
          </div>
        )}

      {works.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Body size="sm" bold style={{ marginBottom: 8 }}>
            Top suggested {pluralize('reference', works.length)}
          </Body>
          <div>
            {works.map((work) => (
              <WorkCard
                key={work.work.id}
                workData={work}
                isHighlighted={highlightedWorkId === work.work.id}
                showAddToListButton={true}
                showPaperEvaluationChatButton={showChatButton}
                claimId={claim.id}
                claimText={claim.text}
                pageContext={pageContext}
              />
            ))}
          </div>
        </div>
      )}

      {claim.reference_ids && claim.reference_ids.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Body size="sm" bold style={{ marginBottom: 4 }}>
            Your current references
          </Body>
          <ol>
            {claim.reference_ids.map((refId) => {
              const reference = references.find(
                (r) => r.reference_identifier === refId,
              );
              if (!reference) return null;
              return (
                <li key={refId}>
                  <ReferenceLink
                    key={refId}
                    onClick={() => onReferenceScroll(refId)}
                  >
                    [{refId}] {reference.work.title}
                  </ReferenceLink>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </Card>
  );
};

export default ClaimCard;
